/**
 * Client-side image downscaling.
 *
 * A modern phone camera produces 4-12 MB JPEGs. Uploading those from a field
 * connection is slow enough that agents will abandon the capture, and the API
 * rejects anything over 6 MB anyway. Downscaling in the browser turns a
 * typical photo into 150-400 KB with no meaningful loss for proof purposes -
 * we need to see a battery and a doorway, not read a serial number at 12 MP.
 *
 * This is also why capture uses a file input with the `capture` attribute
 * rather than getUserMedia: the native camera UI is what users already know,
 * it works on iOS Safari (where getUserMedia in a cross-origin context has
 * historically been unreliable), and there is no video stream to leak.
 */

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.82;

export async function downscaleToDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("That file is not an image.");
  }

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process the photo on this device.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  if ("close" in bitmap) bitmap.close();

  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap honours EXIF orientation with imageOrientation set,
  // which matters: a photo taken in portrait otherwise arrives sideways.
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Fall through to the <img> path on browsers that reject the options.
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that photo."));
    };
    img.src = url;
  });
}

export function approximateDataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  return Math.round((base64.length * 3) / 4);
}
